if (!customElements.get('marketing-form')) {
  customElements.define(
    'marketing-form',
    class MarketingForm extends HTMLElement {
      /** @type {HTMLFormElement | null} */
      form = null;

      connectedCallback() {
        this.form = this.querySelector('form');

        if (this.form) {
          this.form.addEventListener('submit', this.#submitHandler);
        }
      }

      disconnectedCallback() {
        if (this.form) {
          this.form.removeEventListener('submit', this.#submitHandler);
        }
      }

      /**
       * @param {Event} e - The form submit event
       * @returns {Promise<void>}
       */
      #submitHandler = async (e) => {
        e.preventDefault();
        e.stopPropagation();

        if (!this.form) return;

        this.form.dataset.loading = 'true';
        this.#showErrors([]);

        try {
          /** @type {any} */
          const formElements = this.form.elements;
          const email = (formElements['email'] || formElements['contact[email]'])?.value?.trim();
          const interest = formElements['interest']?.value?.trim();

          if (!email) {
            this.#showErrors(['Please fill in all required fields']);
            return;
          }

          const provider = this.dataset.provider || 'klaviyo';

          if (provider === 'shopify') {
            await this.#sendToShopify(interest);
          } else {
            await this.#sendToKlaviyo({ email, interest }, this.form.id);
          }

          this.dataset.success = 'true';
        } catch (/** @type {any} */ error) {
          if (error.name === 'AbortError') return;

          window.Sentry?.captureException(error, {
            tags: {
              feature: 'marketing-form',
              error_type: error.name || 'Error',
            },
            extra: {
              formId: this.form.id,
              provider: this.dataset.provider,
              status: error.status,
            },
          });

          this.#showErrors(['Something went wrong. Please try again later.']);
        } finally {
          if (this.form) this.form.dataset.loading = 'false';
        }
      };

      /**
       * @param {string | undefined} interest - Selected interest value
       * @returns {Promise<void>}
       */
      async #sendToShopify(interest) {
        if (!this.form) return;

        const tagsInput = this.form.querySelector('[name="contact[tags]"]');
        if (tagsInput instanceof HTMLInputElement) {
          tagsInput.value = interest ? `newsletter,interest:${interest}` : 'newsletter';
        }

        const interestInputs = this.form.querySelectorAll('[name="interest"]');

        for (const input of interestInputs) {
          if (input instanceof HTMLInputElement) input.disabled = true;
        }

        try {
          await this.#submitFormViaIframe(this.form);
        } finally {
          for (const input of interestInputs) {
            if (input instanceof HTMLInputElement) input.disabled = false;
          }
        }
      }

      /**
       * Native form posts to /contact fail via fetch when Shopify spam protection
       * expects a real navigation. Submit into a hidden iframe instead.
       * @param {HTMLFormElement} form - The marketing form element
       * @returns {Promise<void>}
       */
      async #submitFormViaIframe(form) {
        const frameName = `MarketingFormFrame-${Date.now()}`;
        const iframe = document.createElement('iframe');
        iframe.name = frameName;
        iframe.src = 'about:blank';
        iframe.setAttribute('aria-hidden', 'true');
        iframe.tabIndex = -1;
        iframe.style.cssText = 'position:absolute;width:0;height:0;border:0;clip:rect(0 0 0 0);';

        const previousTarget = form.getAttribute('target');

        /** @returns {Promise<void>} */
        function waitForLoad() {
          return new Promise((resolve) => {
            iframe.addEventListener('load', () => resolve(undefined), { once: true });
          });
        }

        /** Restores the form target and removes the temporary iframe. */
        function cleanup() {
          if (previousTarget === null) form.removeAttribute('target');
          else form.setAttribute('target', previousTarget);
          iframe.remove();
        }

        try {
          const blankLoaded = waitForLoad();
          document.body.appendChild(iframe);
          await blankLoaded;

          form.setAttribute('target', frameName);
          const responseLoaded = waitForLoad();
          form.submit();

          await Promise.race([
            responseLoaded,
            new Promise((_, reject) => {
              window.setTimeout(() => reject(new Error('Shopify marketing form timed out')), 15000);
            }),
          ]);

          let href = '';

          try {
            href = iframe.contentWindow?.location?.href ?? '';
          } catch {
            throw new Error('Shopify marketing form submission could not be verified');
          }

          if (href.includes('/challenge') || href.includes('captcha')) {
            throw new Error('Shopify captcha challenge required');
          }

          if (!href.includes('customer_posted=true')) {
            throw new Error('Shopify marketing form submission failed');
          }
        } finally {
          cleanup();
        }
      }

      /**
       * @param {import('./types').Customer} customer - The customer to send to Klaviyo
       * @param {string} id - The form ID
       * @returns {Promise<void>}
       */
      async #sendToKlaviyo(customer, id) {
        const companyId = this.dataset.companyId;
        const listId = this.dataset.listId;

        if (!companyId || !listId) {
          throw new Error('Missing Klaviyo company or list ID');
        }

        /** @type {Record<string, unknown>} */
        const profileAttributes = {
          email: customer.email,
          subscriptions: {
            email: {
              marketing: {
                consent: 'SUBSCRIBED',
              },
            },
          },
        };

        if (customer.firstName) profileAttributes.first_name = customer.firstName;
        if (customer.lastName) profileAttributes.last_name = customer.lastName;
        if (customer.id) profileAttributes.external_id = customer.id;

        /** @type {Record<string, string>} */
        const properties = {};

        if (customer.birthday) properties.Birthday = customer.birthday;
        if (customer.interest) properties.Interest = customer.interest;

        if (Object.keys(properties).length > 0) profileAttributes.properties = properties;

        const payload = {
          data: {
            type: 'subscription',
            attributes: {
              profile: {
                data: {
                  type: 'profile',
                  attributes: profileAttributes,
                },
              },
              custom_source: id,
            },
            relationships: {
              list: {
                data: {
                  type: 'list',
                  id: listId,
                },
              },
            },
          },
        };

        const response = await fetch(`https://a.klaviyo.com/client/subscriptions?company_id=${companyId}`, {
          method: 'POST',
          headers: {
            Accept: 'application/vnd.api+json',
            Revision: '2025-01-15',
            'Content-Type': 'application/vnd.api+json',
          },
          body: JSON.stringify(payload),
        });

        if (!response.ok) {
          const errorData = await response.json();
          /** @type {any} */
          const error = new Error(errorData.errors?.[0]?.detail || 'Unknown error');
          error.status = response.status;
          throw error;
        }
      }

      /** @param {string[]} errors - The errors to display */
      #showErrors(errors) {
        const errorContainer = this.querySelector('.form-errors');

        if (!errorContainer) return;

        errorContainer.innerHTML = errors.map((/** @type {string} */ error) => `<li>${error}</li>`).join('');
      }
    },
  );
}
